import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import type { Role } from '@/types/schemas';

type AuthContextValue = {
  user: User | null;
  role: Role | null;
  storeIds: string[];
  displayName: string;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [storeIds, setStoreIds] = useState<string[]>([]);
  const [displayName, setDisplayName] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          const tokenResult = await u.getIdTokenResult(true);
          const claimRole = (tokenResult.claims.role as Role | undefined) ?? null;
          setRole(claimRole);
          setDisplayName(u.displayName ?? u.email ?? '');
        } catch (err) {
          console.error('failed to read claims', err);
          setRole(null);
        }
      } else {
        setRole(null);
        setStoreIds([]);
        setDisplayName('');
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Subscribe to user doc for role/storeIds mirror (reactive role updates)
  useEffect(() => {
    if (!user) return;
    const ref = doc(db, 'users', user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data();
        if (data) {
          if (data.role) setRole(data.role as Role);
          if (Array.isArray(data.storeIds)) setStoreIds(data.storeIds);
          if (data.displayName) setDisplayName(data.displayName);
        }
      },
      () => {
        // ignore permission errors before role is set
      },
    );
    return () => unsub();
  }, [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role,
      storeIds,
      displayName,
      loading,
      async signIn(email, password) {
        await signInWithEmailAndPassword(auth, email, password);
      },
      async signUp(email, password) {
        await createUserWithEmailAndPassword(auth, email, password);
      },
      async signOut() {
        await fbSignOut(auth);
      },
    }),
    [user, role, storeIds, displayName, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useRole(): Role | null {
  return useAuth().role;
}

export function useHasRole(...roles: Role[]): boolean {
  const role = useRole();
  return role !== null && roles.includes(role);
}
